import { useRef, useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import api, { API } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import {
  FileArrowUp,
  FilePdf,
  FileDoc,
  FileXls,
  FileText,
  Image as ImageIcon,
  MusicNotes,
  VideoCamera,
  DownloadSimple,
  Trash,
  File as FileGeneric,
} from "@phosphor-icons/react";

const ACCEPT = ".pdf,.doc,.docx,.xls,.xlsx,.txt,.png,.jpg,.jpeg,.webp,.heic,.heif,.gif,.mp3,.wav,.m4a,.ogg,.mp4,.mov,.webm,.avi";

function iconFor(kind, ext) {
  if (kind === "image") return <ImageIcon size={18} weight="duotone" className="text-blue-800" />;
  if (kind === "audio") return <MusicNotes size={18} weight="duotone" className="text-purple-700" />;
  if (kind === "video") return <VideoCamera size={18} weight="duotone" className="text-rose-700" />;
  if (ext === "pdf") return <FilePdf size={18} weight="duotone" className="text-red-700" />;
  if (["doc", "docx"].includes(ext)) return <FileDoc size={18} weight="duotone" className="text-blue-700" />;
  if (["xls", "xlsx"].includes(ext)) return <FileXls size={18} weight="duotone" className="text-emerald-700" />;
  if (ext === "txt") return <FileText size={18} weight="duotone" className="text-slate-700" />;
  return <FileGeneric size={18} weight="duotone" className="text-slate-500" />;
}

function fmtSize(bytes) {
  if (!bytes && bytes !== 0) return "";
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}

export default function FilesPanel({ entityType, entityId }) {
  const qc = useQueryClient();
  const fileRef = useRef(null);
  const [dragOver, setDragOver] = useState(false);
  const [uploadingCount, setUploadingCount] = useState(0);

  const key = ["attachments", entityType, entityId];
  const { data: files = [], isLoading } = useQuery({
    queryKey: key,
    queryFn: async () => (await api.get(`/files?entity_type=${entityType}&entity_id=${entityId}`)).data,
    enabled: !!entityId,
  });

  const del = useMutation({
    mutationFn: async (file_id) => (await api.delete(`/files/${file_id}`)).data,
    onSuccess: () => { qc.invalidateQueries({ queryKey: key }); toast.success("File removed"); },
    onError: (e) => toast.error(e?.response?.data?.detail || "Delete failed"),
  });

  const doUpload = async (fileList) => {
    if (!fileList?.length) return;
    setUploadingCount(fileList.length);
    let ok = 0, failed = 0;
    for (const f of fileList) {
      const fd = new FormData();
      fd.append("file", f);
      fd.append("entity_type", entityType);
      fd.append("entity_id", entityId);
      try {
        const r = await fetch(`${API}/files`, { method: "POST", body: fd, credentials: "include" });
        if (!r.ok) {
          const err = await r.json().catch(() => ({}));
          throw new Error(err.detail || "Upload failed");
        }
        ok++;
      } catch (e) {
        failed++;
        toast.error(`${f.name}: ${e.message}`);
      }
    }
    setUploadingCount(0);
    if (ok) toast.success(`${ok} file${ok > 1 ? "s" : ""} uploaded`);
    if (fileRef.current) fileRef.current.value = "";
    qc.invalidateQueries({ queryKey: key });
  };

  const view = async (f) => {
    const r = await fetch(`${API}/files/${f.file_id}`, { credentials: "include" });
    if (!r.ok) return toast.error("Failed to load");
    const blob = await r.blob();
    const url = URL.createObjectURL(blob);
    // Download for anything except previewable types
    if (["image", "audio", "video"].includes(f.kind) || f.filename?.toLowerCase().endsWith(".pdf")) {
      window.open(url, "_blank");
    } else {
      const a = document.createElement("a");
      a.href = url; a.download = f.filename;
      a.click();
    }
  };

  if (!entityId) {
    return <div className="text-xs text-slate-500 italic">Save the record first to attach files.</div>;
  }

  return (
    <div className="space-y-3" data-testid={`files-panel-${entityType}-${entityId}`}>
      <div className="flex items-center justify-between">
        <div className="text-[11px] uppercase tracking-[0.15em] text-slate-500 font-semibold">Attachments · {files.length}</div>
        <div className="text-[10px] text-slate-400">Max 25MB · docs / images / audio / video</div>
      </div>

      {/* Drop zone */}
      <div
        onDragOver={(e) => { e.preventDefault(); setDragOver(true); }}
        onDragLeave={() => setDragOver(false)}
        onDrop={(e) => { e.preventDefault(); setDragOver(false); doUpload(Array.from(e.dataTransfer.files || [])); }}
        onClick={() => fileRef.current?.click()}
        className={`border-2 border-dashed rounded-md px-4 py-6 text-center cursor-pointer transition-colors ${dragOver ? "border-blue-500 bg-blue-50" : "border-slate-300 bg-slate-50 hover:bg-slate-100"}`}
        data-testid={`files-dropzone-${entityType}`}
      >
        <FileArrowUp size={22} weight="duotone" className="mx-auto text-blue-800" />
        <div className="text-sm text-slate-700 mt-1">
          {uploadingCount > 0 ? `Uploading ${uploadingCount}…` : "Drop files here or click to browse"}
        </div>
      </div>
      <input ref={fileRef} type="file" accept={ACCEPT} multiple className="hidden"
             onChange={(e) => doUpload(Array.from(e.target.files || []))}
             data-testid={`files-input-${entityType}`} />

      {/* File list */}
      {isLoading ? (
        <div className="text-xs text-slate-400 py-4 text-center">Loading files…</div>
      ) : files.length === 0 ? (
        <div className="text-xs text-slate-400 py-4 text-center">No files yet.</div>
      ) : (
        <ul className="border border-slate-200 rounded-md divide-y bg-white" data-testid={`files-list-${entityType}`}>
          {files.map((f) => {
            const ext = f.filename?.split(".").pop()?.toLowerCase() || "";
            return (
              <li key={f.file_id} className="flex items-center gap-3 px-3 py-2 hover:bg-slate-50">
                <div>{iconFor(f.kind, ext)}</div>
                <div className="flex-1 min-w-0">
                  <div className="text-sm text-slate-900 truncate">{f.filename}</div>
                  <div className="text-[11px] text-slate-500">
                    {f.kind} · {fmtSize(f.size)} · uploaded {new Date(f.uploaded_at).toLocaleDateString()} by {f.uploaded_by?.split("@")[0]}
                  </div>
                </div>
                <Button size="sm" variant="ghost" className="h-7 px-2" onClick={() => view(f)} data-testid={`file-view-${f.file_id}`}>
                  <DownloadSimple size={14} />
                </Button>
                <Button size="sm" variant="ghost" className="h-7 px-2 text-red-600" onClick={() => del.mutate(f.file_id)} data-testid={`file-delete-${f.file_id}`}>
                  <Trash size={14} />
                </Button>
              </li>
            );
          })}
        </ul>
      )}
    </div>
  );
}
